import { call, put, takeLatest } from "redux-saga/effects";
import { replace } from "connected-react-router";
import { types, selectors as cedipSelectors } from "reducers/cedip";
import { selectors as sessionSelectors } from "reducers/session";
import { handleError } from "sagas";
import * as i18n from "util/i18n";
import { store } from "store";
import { transactionStatus } from "pages/transactions/constants";
import { downloadPdf, downloadXls } from "util/download";
import { credentialsErrorMessage } from "util/validateMessage";

import * as cedipsMiddleware from "middleware/cedip";

import { actions as notificationActions } from "reducers/notification";

const sagas = [
    takeLatest(types.FILTER_CEDIP_LIST, filterCedipList),
    takeLatest(types.DOWNLOAD_DETAIL_CEDIP_REQUEST, downloadCedip),
    takeLatest(types.ACTION_CEDIP, actionCedip),
    takeLatest(types.ACTION_CEDIP_ACCEPT_TERM, acceptTermTransmitCedip),
    takeLatest(types.TRANSMIT_CEDIP_PREVIEW, getTermTransmitCedip),
];

export default sagas;

const ticketStates = {
    COR020W: "ERROR",
    COR023I: transactionStatus.PENDING,
    COR000I: transactionStatus.FINISHED,
    COR013I: transactionStatus.SCHEDULED,
    COR060I: transactionStatus.PROCESSING,
    API515I: transactionStatus.PENDING,
};

function* filterCedipList({ filters }) {
    try {
        const expiredCheck = !!(filters.expiredFrom && filters.expiredTo);
        const emitedCheck = !!(filters.createdFrom && filters.createdTo);
        const params = { ...filters, expiredCheck, emitedCheck };
        const response = yield call(cedipsMiddleware.filterCedipsList, params);
        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["cedips"]));
        } else if (response.data.data.codigo !== "2400") {
            yield put(notificationActions.showNotification(response.data.data.description, "error", ["cedips"]));
        } else {
            yield put({
                type: types.SET_CEDIP_LIST,
                cedipList: response.data.data.cedips,
                totalPages: response.data.data.totalPages,
                pageNumber: response.data.data.pageNumber,
            });
            yield put({
                type: types.SET_CEDIP_FETCHING,
            });
        }
    } catch (error) {
        const _error = {
            ...error,
            data: {
                ...error.data,
                message: error?.data?.data?.message || error?.data?.message,
            },
        };
        yield call(handleError, _error);
    }
}

function* getTermTransmitCedip() {
    const { type, data } = yield call(cedipsMiddleware.sendTransmitCedipPreview);

    if (type === "W") {
        yield put({ type: types.TRANSMIT_CEDIP_PREVIEW_FAILURE });
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["transmit.cedip"]),
        );
    } else {
        yield put({
            type: types.TRANSMIT_CEDIP_PREVIEW_SUCCESS,
            generalConditionsShowTransmit: data?.data?.generalConditionsShowTransmit,
        });
    }
}

function* acceptTermTransmitCedip() {
    const { type } = yield call(cedipsMiddleware.acceptTermTransmitCedip);

    if (type === "W") {
        yield put({ type: types.ACTION_CEDIP_ACCEPT_TERM_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["cedips"]));
    } else {
        yield put({ type: types.ACTION_CEDIP_ACCEPT_TERM_SUCCESS });
    }
}

function* downloadCedip({ cedip, format }) {
    const { type, data } = yield call(cedipsMiddleware.downloadDetailCedip, cedip, format);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_DETAIL_CEDIP_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["cedips"]));
    } else {
        const { content, fileName } = data.data;

        if (format === "pdf") {
            downloadPdf(fileName, content);
        } else {
            downloadXls(fileName, content);
        }

        yield put({ type: types.DOWNLOAD_DETAIL_CEDIP_SUCCESS });
    }
}

function* actionCedip({ transmitCedipDataTemp, formikBag, credentialType }) {
    const { userId: idUser } = sessionSelectors.getUser(store.getState());
    const selectedCedip = cedipSelectors.getSelectedCedip(store.getState());
    const selectedAction = cedipSelectors.getSelectedAction(store.getState());
    const transmitCedipData = cedipSelectors.getTransmitCedipData(store.getState());
    const { amount } = transmitCedipData;

    try {
        let response;
        let params;

        if (selectedAction === "CEDIP-TRANSMIT") {
            if (transmitCedipData.idTransaction) {
                params = { ...transmitCedipDataTemp, ...transmitCedipData };
            } else {
                params = {
                    ...transmitCedipDataTemp,
                    selectedCedip,
                    amount: {
                        currency: selectedCedip.tipoMoneda,
                        quantity: amount,
                    },
                };
            }
            response = yield call(cedipsMiddleware.signTransmitCedip, params);
        }

        params = {
            idUser,
            ...transmitCedipDataTemp,
            selectedCedip,
            amount,
        };
        if (selectedAction === "CEDIP-TRANSMIT-ADMIT") {
            response = yield call(cedipsMiddleware.sendTransmitAdmitCedip, params);
        }
        if (selectedAction === "CEDIP-TRANSMIT-ANULATE") {
            response = yield call(cedipsMiddleware.sendTransmitAnulateCedip, params);
        }
        if (selectedAction === "CEDIP-TRANSMIT-REPUDIATE") {
            response = yield call(cedipsMiddleware.sendTransmitRepudiateCedip, params);
        }

        const defaultMessage = i18n.get("global.unexpectedError");

        if (response.type === "W" && response.data?.code !== "API516W") {
            if (response.data?.code === "COR020W") {
                const message = credentialsErrorMessage(response.data?.data) || defaultMessage;
                yield put(notificationActions.showNotification(message, "error", ["/cedip/transmit/confirm"]));

                if (response.data?.data?.password || response.data?.data?.otp) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";

                    yield put(
                        notificationActions.showNotification(i18n.get(msj), "error", ["/cedip/transmit/confirm"]),
                    );
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
            } else {
                yield put(
                    notificationActions.showNotification(response.data?.message || defaultMessage, "error", [
                        "cedip/transmit/confirm",
                    ]),
                );
            }
        } else if (response.type === "W" && response.data?.code === "API516W") {
            const message = response.data?.data?.description || defaultMessage;

            yield put(notificationActions.showNotification(message, "error", ["/cedip/transmit/confirm"]));
        } else {
            if (selectedAction === "CEDIP-TRANSMIT") {
                yield put({
                    type: types.CEDIP_TICKET_SUCCESS,
                    cedipTicket: {
                        status: ticketStates[response.data.code],
                        ...response.data,
                    },
                });
            }
            if (
                selectedAction === "CEDIP-TRANSMIT-ADMIT" ||
                selectedAction === "CEDIP-TRANSMIT-ANULATE" ||
                selectedAction === "CEDIP-TRANSMIT-REPUDIATE"
            ) {
                yield put(
                    notificationActions.showNotification(i18n.get("activities.cedip.transmit.finished"), "success", [
                        "deposits",
                    ]),
                );
                yield put(
                    replace({
                        pathname: "/depositsCedip",
                        state: { tab: 0 },
                    }),
                );
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}
